import { NavLink, Outlet } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { useHinweise } from '../hinweise/HinweisContext'
import { Wortmarke } from './Wortmarke'

/**
 * Das Zeichen am Menüpunkt.
 *
 * Zahl statt bloßem Punkt: „da ist etwas“ beantwortet die Frage nur halb — ob eine
 * Meldung wartet oder zwanzig, entscheidet, ob man sie sofort ansieht. Ab hundert
 * verliert die genaue Zahl ihren Wert, dann steht „99+“.
 */
function Zeichen({ anzahl }: { anzahl: number }) {
  if (anzahl <= 0) {
    return null
  }
  return (
    <span className="navigation-zeichen" aria-label={`${anzahl} neu`}>
      {anzahl > 99 ? '99+' : anzahl}
    </span>
  )
}

/**
 * Eine Gruppenüberschrift, wahlweise mit der Summe ihrer Zeichen.
 *
 * Die Summe steht an „Eingänge“: Wer die Leiste überfliegt, sieht an einer Stelle, ob
 * überhaupt etwas wartet — ohne vier Zeilen einzeln abzusuchen.
 */
function Gruppe({ name, anzahl }: { name: string; anzahl?: number }) {
  return (
    <div className="navigation-gruppe">
      <span>{name}</span>
      {anzahl !== undefined && anzahl > 0 && (
        <span className="navigation-zeichen" aria-label={`${anzahl} neu insgesamt`}>
          {anzahl > 99 ? '99+' : anzahl}
        </span>
      )}
    </div>
  )
}

export function Geruest() {
  const { benutzer, abmelden, hatRolle } = useAuth()
  const { nachrichten, meldungen, verkehrsmeldungen, klaerungen, tonAn, setzeTon } = useHinweise()

  const klasse = ({ isActive }: { isActive: boolean }) => (isActive ? 'aktiv' : '')
  const darfVerwalten = hatRolle('PLATTFORM_ADMIN', 'ADMIN')
  const istPlattformAdmin = hatRolle('PLATTFORM_ADMIN')
  // Klärungen gehören der Disposition: Das Personalwesen sieht die Gruppe nicht, und die
  // Zahl darf auch nicht in ihre Summe einfließen.
  const darfKlaerungen = hatRolle('ADMIN', 'DISPONENT')

  // Die Werkstatt sieht nur Fahrzeuge, Meldungen und ihren eigenen Schriftwechsel mit den
  // Fahrern - kein Dienstplan, keine Kurse, keine Stammdaten. Wer zusaetzlich disponiert,
  // bekommt die volle Navigation: Die Disposition darf alles sehen.
  const nurWerkstatt =
    hatRolle('WERKSTATT') && !hatRolle('PLATTFORM_ADMIN', 'ADMIN', 'DISPONENT', 'PERSONAL')

  return (
    <div className="geruest">
      <aside className="seitenleiste">
        <div className="seitenleiste-kopf">
          <Wortmarke />
          <small>Bus-Operations</small>
        </div>

        <nav className="navigation">
          {/* Der Plattform-Administrator betreibt die Anwendung und führt Support —
              er hat selbst keine Kurse, Fahrzeuge oder Mitarbeiter. Er sieht deshalb nur
              die Übersicht und die Verwaltung. */}
          {(istPlattformAdmin || nurWerkstatt) && (
            <NavLink to="/" end className={klasse}>
              Übersicht
            </NavLink>
          )}

          {nurWerkstatt && (
            <>
              <Gruppe name="Eingänge" anzahl={nachrichten + meldungen} />
              <NavLink to="/nachrichten" className={klasse}>
                Nachrichten
                <Zeichen anzahl={nachrichten} />
              </NavLink>
              <NavLink to="/meldungen" className={klasse}>
                Meldungen
                <Zeichen anzahl={meldungen} />
              </NavLink>

              <Gruppe name="Stammdaten" />
              <NavLink to="/fahrzeuge" className={klasse}>
                Fahrzeuge
              </NavLink>
            </>
          )}

          {!istPlattformAdmin && !nurWerkstatt && (
            <>
              {/* Was täglich dran ist, steht oben — danach greift man mehrmals am Tag. */}
              <Gruppe name="Heute" />
              <NavLink to="/" end className={klasse}>
                Übersicht
              </NavLink>
              <NavLink to="/disposition" className={klasse}>
                Disposition
              </NavLink>
              <NavLink to="/dienstplan" className={klasse}>
                Dienstplan
              </NavLink>
              <NavLink to="/ausfall" className={klasse}>
                Ausfallhilfe
              </NavLink>

              {/* Alles, was von außen hereinkommt und beantwortet werden will. Nur hier
                  stehen Zeichen, und die Gruppe trägt ihre Summe. */}
              <Gruppe
                name="Eingänge"
                anzahl={nachrichten + meldungen + verkehrsmeldungen + (darfKlaerungen ? klaerungen : 0)}
              />
              <NavLink to="/nachrichten" className={klasse}>
                Nachrichten
                <Zeichen anzahl={nachrichten} />
              </NavLink>
              <NavLink to="/meldungen" className={klasse}>
                Meldungen
                <Zeichen anzahl={meldungen} />
              </NavLink>
              <NavLink to="/verkehrsmeldungen" className={klasse}>
                Verkehrsmeldungen
                <Zeichen anzahl={verkehrsmeldungen} />
              </NavLink>
              {darfKlaerungen && (
                <NavLink to="/klaerungen" className={klasse}>
                  Klärungen
                  <Zeichen anzahl={klaerungen} />
                </NavLink>
              )}

              {/* Seltener, dafür langlebig: woraus sich der Dienstplan überhaupt bildet. */}
              <Gruppe name="Planung" />
              <NavLink to="/turnusse" className={klasse}>
                Turnusse
              </NavLink>
              <NavLink to="/kurse" className={klasse}>
                Kurse
              </NavLink>
              <NavLink to="/bibliothek" className={klasse}>
                Kursbibliothek
              </NavLink>
              <NavLink to="/fahrplanwechsel" className={klasse}>
                Fahrplanwechsel
              </NavLink>
              <NavLink to="/umlaufbildung" className={klasse}>
                Umlaufbildung
              </NavLink>
              <NavLink to="/analyse" className={klasse}>
                Dienstanalyse
              </NavLink>

              <Gruppe name="Stammdaten" />
              <NavLink to="/fahrer" className={klasse}>
                Personal
              </NavLink>
              <NavLink to="/fahrzeuge" className={klasse}>
                Fahrzeuge
              </NavLink>
              <NavLink to="/teams" className={klasse}>
                Teams
              </NavLink>
              <NavLink to="/betriebshoefe" className={klasse}>
                Betriebshöfe
              </NavLink>
              <NavLink to="/stammdaten-import" className={klasse}>
                Daten übernehmen
              </NavLink>
            </>
          )}

          {istPlattformAdmin ? (
            <>
              <Gruppe name="Betrieb" />
              <NavLink to="/mandanten" className={klasse}>
                Mandanten
              </NavLink>
              <NavLink to="/zugaenge" className={klasse}>
                Zugänge
              </NavLink>

              <Gruppe name="Konfiguration" />
              <NavLink to="/konfiguration" className={klasse}>
                Dateiablage
              </NavLink>
              <NavLink to="/parameter" className={klasse}>
                Parameter
              </NavLink>
              <NavLink to="/posteingang" className={klasse}>
                Posteingang
              </NavLink>
              <NavLink to="/betriebseinstellungen" className={klasse}>
                Betriebseinstellungen
              </NavLink>
              <NavLink to="/mail" className={klasse}>
                Mail-Einstellungen
              </NavLink>
              <NavLink to="/mailvorlagen" className={klasse}>
                Mail-Vorlagen
              </NavLink>
            </>
          ) : (
            darfVerwalten && (
              <>
                <Gruppe name="Verwaltung" />
                <NavLink to="/zugaenge" className={klasse}>
                  Zugänge
                </NavLink>
                <NavLink to="/konfiguration" className={klasse}>
                  Konfiguration
                </NavLink>
                <NavLink to="/betriebseinstellungen" className={klasse}>
                  Betriebseinstellungen
                </NavLink>
              </>
            )
          )}
        </nav>

        <div className="seitenleiste-fuss">
          <div className="benutzername">{benutzer?.anzeigename}</div>
          <div className="mandant">{benutzer?.mandantName}</div>
          {/* In der Zentrale läuft die Anwendung den ganzen Tag. Wer nebendran
              telefoniert, will den Ton abstellen können, ohne den Hinweis zu verlieren. */}
          <button
            type="button"
            className={`tonschalter${tonAn ? '' : ' aus'}`}
            onClick={() => setzeTon(!tonAn)}
            title={
              tonAn
                ? 'Kein Ton mehr bei neuen Eingängen'
                : 'Bei neuen Eingängen einen kurzen Ton geben'
            }
          >
            ♪ {tonAn ? 'Ton an' : 'Ton aus'}
          </button>
          <button
            className="knopf-schlicht"
            style={{ marginTop: 10, width: '100%' }}
            onClick={abmelden}
          >
            Abmelden
          </button>
        </div>
      </aside>

      <main className="inhalt">
        <Outlet />
      </main>
    </div>
  )
}
